import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { uniqBy } from "lodash-es";

export type LoadingStatus = "incomplete" | "fetching" | "complete";

interface State {
  loadingStatus: LoadingStatus;
  memos: Memo[];
  commentCounts: Record<string, number>;
}

const memoSlice = createSlice({
  name: "memo",
  initialState: {
    loadingStatus: "incomplete",
    memos: [],
    commentCounts: {},
  } as State,
  reducers: {
    updateLoadingStatus: (state, action: PayloadAction<LoadingStatus>) => {
      return {
        ...state,
        loadingStatus: action.payload,
      };
    },
    upsertMemos: (state, action: PayloadAction<Memo[]>) => {
      return {
        ...state,
        memos: uniqBy([...action.payload, ...state.memos], "id"),
      };
    },
    replaceMemos: (state, action: PayloadAction<Memo[]>) => {
      return {
        ...state,
        memos: uniqBy(action.payload, "id"),
      };
    },
    createMemo: (state, action: PayloadAction<Memo>) => {
      return {
        ...state,
        memos: state.memos.concat(action.payload),
      };
    },
    setCommentCnt: (state, action: PayloadAction<Record<string, number>>) => {
      return {
        ...state,
        commentCounts: action.payload,
      };
    },
    patchMemo: (state, action: PayloadAction<Partial<Memo>>) => {
      return {
        ...state,
        memos: state.memos
          .map((memo) => {
            if (memo.id === action.payload.id) {
              return {
                ...memo,
                ...action.payload,
              };
            } else {
              return memo;
            }
          })
          .filter((memo) => memo.rowStatus === "NORMAL"),
      };
    },
    deleteMemo: (state, action: PayloadAction<MemoId>) => {
      return {
        ...state,
        memos: state.memos.filter((memo) => {
          return memo.id !== action.payload;
        }),
      };
    },
  },
});

export const { updateLoadingStatus, upsertMemos, replaceMemos, createMemo, patchMemo, deleteMemo, setCommentCnt } = memoSlice.actions;

export default memoSlice.reducer;
